<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { T } from '@tolgee/vue'
import BaseButton from '@/components/base/BaseButton.vue'
import type { ExtraSpinCounts } from '@/types/fortune-wheel'
import ThePremium from '@/components/ThePremium.vue'
import { computed } from 'vue'
import { useConfig } from '@/composables/game/config'
import SparkingWheelImage from '@/assets/images/gifs/wheel.gif'
import { useNow } from '@/composables/game/time'
import { useEcommerce, useEcommercePrice } from '@/composables/game/ecommerce'
import CrossIcon from '@/components/icons/CrossIcon.vue'
import { formatDuration } from '@/helpers/date'

defineProps<{
	show: boolean,
	spinUpgrades: ExtraSpinCounts
}>()

const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'purchaseDone'): void
}>()

const { config } = useConfig()
const { counterToNextDay } = useNow()
const { purchase } = useEcommerce()
const { getStarPrice } = useEcommercePrice()

const item = computed(() => config.value.game.ecommerce.items.find(item => item.id === 'spin.25.100'))
const itemStarPrice = computed(() => item.value ? getStarPrice(item.value) : null)

const dailySpinsCount = computed(() => config.value.game.fortuneWheel.extraSpin.daily.amount)
const countdown = computed(() => formatDuration(counterToNextDay.value))

async function onBuySpins() {
  if (item.value) {
    const { paid } = await purchase({ type: 'item', item: item.value })
    if (paid) {
      emit('purchaseDone')
    }
  }

}
</script>
<template>
  <TheBottomSheet
    v-if="item"
    :show="show"
    outer-content-class="[background-image:url('@/assets/images/blue-modal-background.png')] bg-cover backdrop-blur-[50px] w-max items-center justify-center overflow-visible"
    content-class="pt-8 pb-3 [background-image:url('@/assets/images/blue-modal-background.png')] bg-cover items-center justify-center overflow-visible w-[276px]"
    border-container-class="overflow-visible"
    @hide="$emit('hide')"
  >
    <template #closeButton>
      <div
        class="absolute w-6 h-6 text-black top-0 right-0 z-3 bg-blue-solid rounded-full border-2 border-blue-lightgray flex items-center justify-center"
        @click="$emit('hide')"
      >
        <CrossIcon class="text-black [&>path]:stroke-[3px] size-[12px]" />
      </div>
    </template>
    <div class="flex flex-col items-center w-max">
      <h2 class="text-black text-[36px] leading-[48px] -tracking-[0.72px] font-bold mb-3 white-stroke">
        <T
          key-name="fortune_wheel.info.out_of_spins.title"
          default-value="Out of spins?"
        />
      </h2>
      <div class="flex flex-col items-center mb-8">
        <img
          :src="SparkingWheelImage"
          class="w-20 h-auto"
        >
        <span class="text-[32px] leading-8 font-black black-stroke -mt-8">{{ `+${item.benefits[0].amount}` }}</span>
      </div>
      <BaseButton
        v-if="itemStarPrice"
        @click="onBuySpins"
        class="mb-2"
      >
        <div class="flex items-center justify-center gap-x-2">
          <div class="flex items-center justify-center gap-x-1.5">
            <ThePremium :with-tooltip="false" />
            <span>{{ itemStarPrice.amount }}</span>
          </div>
          <span>(${{ itemStarPrice.usdAmount }})</span>
        </div>
      </BaseButton>
      <p class="text-center text-tropee-black text-base font-semibold">
        <T
          key-name="fortune_wheel.out_of_spins.or_wait"
          default-value="Or wait"
        />
        <span class="font-bold">
          {{ ` ${countdown} ` }}
        </span>
        <T
          key-name="fortune_wheel.out_of_spins.for_spins"
          default-value="for {count, plural, one {# spin} other {# spins}}"
          :params="{count: dailySpinsCount}"
        />
      </p>
    </div>
  </TheBottomSheet>
</template>
<style scoped>
.black-stroke {
	-webkit-text-stroke: 1px black;
}

.white-stroke {
	text-shadow: -2px -2px 0 rgba(255, 255, 255, 0.40),
	2px -2px 0 rgba(255, 255, 255, 0.40),
	-2px 2px 0 rgba(255, 255, 255, 0.40),
	2px 2px 0 rgba(255, 255, 255, 0.40),
	-2.5px 0 0 rgba(255, 255, 255, 0.40),
	2.5px 0 0 rgba(255, 255, 255, 0.40),
	0 -2.5px 0 rgba(255, 255, 255, 0.40),
	0 2.5px 0 rgba(255, 255, 255, 0.40),
	-1px -1px 0 rgba(255, 255, 255, 0.40),
	1px -1px 0 rgba(255, 255, 255, 0.40),
	-1px 1px 0 rgba(255, 255, 255, 0.40),
	1px 1px 0 rgba(255, 255, 255, 0.40);
}

</style>
